import React, { useState, useRef } from 'react';
import { Upload, X, File, FileText, Image as ImageIcon } from 'lucide-react';
import './FileAttachment.css';

interface FileAttachmentProps {
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  acceptedTypes?: string; // e.g., ".pdf,.doc,.docx,.txt"
  existingFiles?: string[]; // URLs of existing files
}

interface FilePreview {
  file: File;
  url: string;
  type: string;
}

const FileAttachment: React.FC<FileAttachmentProps> = ({
  onFilesChange,
  maxFiles = 10,
  acceptedTypes = '*',
  existingFiles = []
}) => {
  const [files, setFiles] = useState<FilePreview[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext || '')) {
      return <ImageIcon size={20} />;
    }
    if (['pdf'].includes(ext || '')) {
      return <FileText size={20} />;
    }
    return <File size={20} />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles: File[]) => {
    const remainingSlots = maxFiles - files.length - existingFiles.length;
    const filesToAdd = newFiles.slice(0, remainingSlots);

    const newPreviews: FilePreview[] = filesToAdd.map(file => ({
      file,
      url: URL.createObjectURL(file),
      type: file.type
    }));

    const updatedFiles = [...files, ...newPreviews];
    setFiles(updatedFiles);
    onFilesChange(updatedFiles.map(f => f.file));
  };

  const removeFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);

    // Clean up object URL to prevent memory leaks
    URL.revokeObjectURL(files[index].url);

    setFiles(updatedFiles);
    onFilesChange(updatedFiles.map(f => f.file));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const totalFiles = files.length + existingFiles.length;
  const canAddMore = totalFiles < maxFiles;

  return (
    <div className="file-attachment">
      <div className="file-attachment-controls">
        {canAddMore && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleUploadClick}
            title="Attach files"
          >
            <Upload size={18} />
            Attach Files
          </button>
        )}
        <span className="file-count">
          {totalFiles} / {maxFiles} files
        </span>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* File list */}
      {(existingFiles.length > 0 || files.length > 0) && (
        <div className="file-list">
          {/* Existing files */}
          {existingFiles.map((url, index) => {
            const fileName = url.split('/').pop() || 'existing-file';
            return (
              <div key={`existing-${index}`} className="file-item">
                {getFileIcon(fileName)}
                <div className="file-info">
                  <div className="file-name">{fileName}</div>
                  <div className="file-label">Existing</div>
                </div>
              </div>
            );
          })}

          {/* New files */}
          {files.map((filePreview, index) => (
            <div key={`new-${index}`} className="file-item">
              {getFileIcon(filePreview.file.name)}
              <div className="file-info">
                <div className="file-name">{filePreview.file.name}</div>
                <div className="file-size">{formatFileSize(filePreview.file.size)}</div>
              </div>
              <button
                type="button"
                className="file-remove"
                onClick={() => removeFile(index)}
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {totalFiles === 0 && (
        <div className="file-empty-state">
          <File size={32} />
          <p>No files attached</p>
          <p className="file-hint">
            Click "Attach Files" to upload documents, images, or other files
          </p>
        </div>
      )}
    </div>
  );
};

export default FileAttachment;
